import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink2';
import { MatTabGroup } from '@angular/material/tabs';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { RolePermStates } from '../../../role-permissions-controller/states';
import { RolePermActions } from '../../../role-permissions-controller/actions';
import { PermissionStates } from '../../../permission-controller/root';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-role-permission-add',
  templateUrl: './role-permission-add.component.html',
  styleUrls: ['./role-permission-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class RolePermissionAddComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 2;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  // previous index
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  public form: FormGroup;

  // fields that require options
  status: string;
  statusArr: any = [];
  newStatusArr: any = [];

  //translation variable
  title: string;
  back_button: string;
  save_button: string;
  name_text: string;
  please_insert_name_text: string;
  code_text: string;
  code_must_be_unique_text: string;
  please_insert_code_text: string;
  code_should_be_less_than_255_characters_text: string;
  not_a_valid_entry_text: string;
  status_text: string;
  active_text: string;
  inactive_text: string;

  constructor(
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.add_role;
        this.back_button = res.back_button;
        this.save_button = res.save_button;
        this.name_text = res.name;
        this.please_insert_name_text = res.please_insert_name;
        this.code_text = res.code;
        this.code_must_be_unique_text = res.code_must_be_unique;
        this.please_insert_code_text = res.please_insert_code;
        this.code_should_be_less_than_255_characters_text = res.code_should_be_less_than_255_characters;
        this.not_a_valid_entry_text = res.invalid_entry;
        this.status_text = res.status;
        this.active_text = res.active;
        this.inactive_text = res.inactive;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Add Role';
      this.back_button = 'Back';
      this.save_button = 'SAVE';
      this.name_text = 'Name';
      this.please_insert_name_text = 'Please insert name';
      this.code_text = 'Code';
      this.code_must_be_unique_text = 'Code must be unique';
      this.please_insert_code_text = 'Please insert code';
      this.code_should_be_less_than_255_characters_text = 'Code should be less than 255 characters';
      this.not_a_valid_entry_text = 'Not a valid entry';
      this.status_text = 'Status';
      this.active_text = 'ACTIVE';
      this.inactive_text = 'INACTIVE';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
   ngOnInit() {
    this.statusArr = [
      { value: 'ACTIVE', viewValue: this.active_text },  // Use the translated value
      { value: 'INACTIVE', viewValue: this.inactive_text }  // Use the translated value
    ];
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      code: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      currStatus: [''],
      status: ['']
    })

    this.newStatusArr = this.statusArr;

  }

  /**
   * This function is called when the user clicks on the save button.
   * This will dispatch an NgRx action to create a new role.
   *
   */
   onSubmit() {
     this.store.dispatch(RolePermActions.createRoleInit({role: this.form.value}));
     this.onReturn();
   }

 /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
  applyStatusFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newStatusArr = this.statusArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
